import { RadioGroup, Switch } from "@aidkitorg/component-library";
import { RadioGroupOption } from "@aidkitorg/component-library/src/RadioGroup";
import { Block, ConditionalContentList, ExpandedSurvey, RichText, Select, Survey, Text } from "@aidkitorg/types/lib/survey";
import { getComponentIdentifier, getComponentNameTitleOrPath } from "@aidkitorg/types/lib/translation/componentIdentifier";
import { DistroComponent } from "@aidkitorg/types/lib/translation/questions";
import { deepCopy, expandTemplates, expandTemplatesStrict, generateUuid } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { FIELD_REF_REGEX } from "@aidkitorg/types/lib/util";
import { toName } from '@aidkitorg/typesheets/lib/utils';
import { Dialog, Transition } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { captureException } from "@sentry/react";
import { Fragment, useEffect, useState } from "react";
import { SearchableComponent } from "./Config/Util";
import { BUTTON_CLASS } from "./Util";

const DELIMITER = ': ';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

type MatchType = "Name/Title/Path" | "Target Field" | "Content" | "Formula" | "Kind" | "Comments" | "Other";

export type TreeBlock = {
  thing: SearchableComponent,
  tree: string[],
  matchTypes: MatchType[]
};

type SearchOptions = {
  matchCase: boolean,
  wholeMatchOnly: boolean,
  includeDashboards: boolean,
  includeComments: boolean,
}

export function highlightSearchTerm(jsonString: string, searchTerm: string | string[], searchOptions: SearchOptions) {
  let segments: string[] = [];
  const isArrayOfTerms = Array.isArray(searchTerm);
  try {
    let searchTermRegex;
    if (searchOptions.wholeMatchOnly) {
      // Escape special characters in the searchTerm to be used in a regular expression
      if (isArrayOfTerms) {
        const escapedSearchTerms = searchTerm.map(term => term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
        // Create a regular expression with word boundaries to look for instances of the searchTerm token
        searchTermRegex = new RegExp(`\\b(${escapedSearchTerms.join('|')})\\b`, 'gi');
      } else {
        const escapedSearchTerm = searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        // Create a regular expression with word boundaries to look for instances of the searchTerm token
        searchTermRegex = new RegExp(`\\b(${escapedSearchTerm})\\b`, 'gi');
      }
    } else {
      searchTermRegex = new RegExp(`(${isArrayOfTerms ? searchTerm.join('|') : searchTerm})`, 'gi');
    }
    segments = jsonString.split(searchTermRegex);
  } catch (_) {
    // Do nothing for now
  }
  return segments.map((segment, index) => {
    if ((searchOptions.matchCase && (isArrayOfTerms ? searchTerm.some(term => term === segment) : segment === searchTerm))
      || (!searchOptions.matchCase && (isArrayOfTerms ? searchTerm.some(term => term.toLowerCase().includes(segment.toLowerCase())) : segment.toLowerCase() === searchTerm.toLowerCase()))) {
      return (
        <span key={index} style={{ backgroundColor: 'moccasin', fontWeight: 'bold' }}>
          {segment}
        </span>
      );
    }
    return <span key={index}>{segment}</span>;
  });
}



export async function findComponent(survey: ExpandedSurvey['survey'], searchTerm: string, options: SearchOptions): Promise<TreeBlock[] | null> {
  if (!survey || !searchTerm) return null;

  if (!options.matchCase) searchTerm = searchTerm.toLowerCase();

  // Escape special characters in the searchTerm to be used in a regular expression
  const escapedSearchTerm = searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  // Create a regular expression with word boundaries to look for instances of the searchTerm token
  const tokenMatchRegex = new RegExp(`\\b${escapedSearchTerm}\\b`);

  survey = expandTemplates(deepCopy(survey)) as ExpandedSurvey['survey'];

  // This is a set of JSON Stringified TreeBlocks
  const foundThings = new Set<string>();

  function traverse(thing: SearchableComponent, parentTree: string[], isDashboardComponent?: boolean): Block[] {
    if (isDashboardComponent && !options.includeDashboards) return [];

    const componentName = getComponentNameTitleOrPath(thing);

    // pathName is used to describe the path
    const pathName = getComponentIdentifier(thing);

    const tree = [...parentTree, thing.kind + (pathName ? DELIMITER + pathName : '')];
    const thingToAdd: TreeBlock = { thing, tree, matchTypes: [] };

    // TODO: We shouldn't be defining a function within a recursive function like this.
    const maybeAdd = (possibleMatch: string, matchType: TreeBlock['matchTypes'][number]) => {
      if (!options.matchCase) possibleMatch = possibleMatch.toLowerCase();

      if ((options.wholeMatchOnly && tokenMatchRegex.test(possibleMatch)) || (!options.wholeMatchOnly && possibleMatch.includes(searchTerm))) {
        thingToAdd.matchTypes.push(matchType);
      }
    }

    maybeAdd(componentName, "Name/Title/Path");

    const blockKey = ((thing as any).targetField || '');
    maybeAdd(blockKey, "Target Field");

    const content = (thing as any).content?.en || '';
    maybeAdd(content, "Content");

    const choices = JSON.stringify((thing as Select).choices || []);
    maybeAdd(choices, "Content");

    ['formula', 'condition', 'filter', 'sql', 'groups', 'columns'].forEach(key => {
      const unparsedFormula = (thing as any)[key] || '';
      const formula = typeof unparsedFormula === 'object' ? JSON.stringify(unparsedFormula) : unparsedFormula;
      maybeAdd(formula, "Formula");
    });

    const kind = ((thing as any).kind || '');
    maybeAdd(kind, "Kind");

    if (options.includeComments) {
      const comment = ((thing as any)["//"] || '');
      maybeAdd(comment, "Comments");
    }

    if (thing.kind === 'Action') {
      if (thing.fields) {
        for (let field of thing.fields) {
          maybeAdd(field, "Target Field");
        }
      }

      if (thing.action && typeof thing.action !== 'string' && (thing.action as any).targetField) {
        maybeAdd((thing.action as any).targetField, "Target Field");
      }
      if (thing.explanation) {
        maybeAdd(thing.explanation.en || '', "Content");
      }
    }

    if (thing.kind === 'Payment') {
      maybeAdd(thing.cardIdField || '', 'Target Field');
      maybeAdd((typeof thing.ledger === 'string' ? thing.ledger : thing.ledger?.field) || '', 'Content');
      maybeAdd(thing.targetField || '', 'Target Field');
      maybeAdd(thing.amount || '', 'Content');
      maybeAdd(thing.type || '', 'Content');
    }

    function traverseTextOrConditionalContentList(textOrConditionalContentList: RichText | ConditionalContentList) {
      function traverseConditionalContent(textOrConditionalContent: ConditionalContentList) {
        textOrConditionalContent.forEach(textOrContent => {
          if ('en' in textOrContent) {
            maybeAdd(textOrContent.en || '', 'Content');
          } else {
            traverseConditionalContent(textOrContent.components);
            if (textOrContent.otherwise) {
              traverseConditionalContent(textOrContent.otherwise);
            }
          }
        })
      }

      if ('en' in textOrConditionalContentList) {
        maybeAdd(textOrConditionalContentList.en, 'Content');
      } else {
        traverseConditionalContent(textOrConditionalContentList);
      }
    }

    if (thing.kind === 'Notification' || thing.kind === 'InlineNotification') {
      maybeAdd(thing.targetPrefix || '', 'Target Field');
      maybeAdd(JSON.stringify(thing.initial_notification.enabled_when || ''), "Formula");

      traverseTextOrConditionalContentList(thing.initial_notification.message);

      maybeAdd(thing.initial_notification.email_subject.en || '', 'Content');

      if (thing.initial_notification.email_message) {
        traverseTextOrConditionalContentList(thing.initial_notification.email_message);
      }

      if (thing.kind === 'Notification' && thing.followups) {
        thing.followups.forEach((followup) => {
          maybeAdd(followup.suffix || '', 'Target Field');
          maybeAdd(followup.email_subject.en || '', 'Content');
          maybeAdd(JSON.stringify(followup.send_if || ''), "Formula");
          traverseTextOrConditionalContentList(followup.message);
          if (followup.email_message) {
            traverseTextOrConditionalContentList(followup.email_message);
          }
        });
      }
    }

    if (thingToAdd.matchTypes.length) {
      const toAdd = JSON.stringify(thingToAdd);
      foundThings.add(toAdd);
    }

    if (thing.kind === 'Third Party Check') {
      traverse(thing.type, tree);
    }

    if (thing.kind === 'Custom Query') {
      traverse(thing.visualization, tree, true);
    }

    if (thing.kind === 'Subsurvey') {
      thing.sections.forEach((section) => {
        traverse(section, tree);
      })
    }

    if (thing.kind === 'Section') {
      thing.components.forEach(block => {
        traverse(block, tree);
      });
    }
    if (thing.kind === 'Collection') {
      thing.components.forEach(component => {
        traverse(component, tree);
      });
    }

    if (thing.kind === 'Dashboard' || thing.kind === 'Dashboard Section') {
      thing.components.forEach(component => {
        traverse(component, tree, true);
      });
    }

    if (thing.kind === 'Conditional Block') {
      thing.components.forEach(component => {
        traverse(component, tree);
      });
      if (thing.otherwise) {
        thing.otherwise.forEach(component => {
          traverse(component, tree);
        })
      }
    }
    return [];
  }
  (survey || []).forEach(component => {
    traverse(component, []);
  });

  let things: TreeBlock[] = [];
  foundThings.forEach(thing => { things.push(JSON.parse(thing) as TreeBlock); });

  // Rank the components by how close they are to the search term
  things.sort((a, b) => {
    let aName = ((a.thing as any).name?.en || '').toLowerCase();
    let bName = ((b.thing as any).name?.en || '').toLowerCase();
    let aKey = ((a.thing as any).targetField || '').toLowerCase();
    let bKey = ((b.thing as any).targetField || '').toLowerCase();

    // console.log("Comparing", aName, bName, aKey, bKey, name);

    if (!aName && !aKey) return 1;
    if (!bName && !bKey) return -1;

    // Rank ascending top score = BOTH Name and Key include
    // Rank descending top score = EITHER Name or Key include
    if (aKey.startsWith(searchTerm) && bKey.startsWith(searchTerm)) return 0;
    if (aName === searchTerm || aKey === searchTerm || aKey.startsWith(searchTerm)) return -1;
    if (bName === searchTerm || bKey === searchTerm || bKey.startsWith(searchTerm)) return 1;
    if (aName.includes(searchTerm) && aKey.includes(searchTerm) && bName.includes(searchTerm) && bKey.includes(searchTerm)) return 0;
    if (aName.includes(searchTerm) && aKey.includes(searchTerm)) return -1;
    if (bName.includes(searchTerm) && bKey.includes(searchTerm)) return 1;
    if (aName.includes(searchTerm) || aKey.includes(searchTerm)) return -1;
    if (bName.includes(searchTerm) || bKey.includes(searchTerm)) return 1;
    return 0;
  });
  return things;
}

// Note: we could do away with this function if we decide we're okay keeping many references
// to parts of the survey in the return object of findComponent(), i.e. instead of the path as
// array of strings, we return an array of arrays of components along the path to the leaf component.
function getComponentsInPath(survey: SearchableComponent[], path: string[]): SearchableComponent[] {

  const foundComponents: SearchableComponent[] = [];

  const recursiveSearch = (searchableComponent: SearchableComponent, currentDepth: number) => {
    // Check if we're too deep
    if (currentDepth >= path.length) {
      return false;
    }

    let identifier = '';
    const [kind, ...rest] = path[currentDepth]?.split(DELIMITER) || [path[currentDepth], []];

    if (rest.length > 0) {
      identifier = rest.join(DELIMITER);
    }

    // Check if the current component is the correct type. All components have a kind.
    if (!kind || kind !== searchableComponent.kind) {
      return false;
    }

    // Check if the current component is the correct identifier. Some components don't have an identifier.
    if (identifier && identifier !== getComponentIdentifier(searchableComponent)) {
      return false;
    }

    if (currentDepth === path.length - 1) {
      foundComponents.push(searchableComponent);
      return true;
    }


    let nextSearchableComponents: SearchableComponent[] = [];
    switch (searchableComponent.kind) {
      case 'Collection':
        nextSearchableComponents = searchableComponent.components;
        break;
      case 'Section':
        nextSearchableComponents = searchableComponent.components;
        break;
      case 'Subsurvey':
        nextSearchableComponents = searchableComponent.sections;
        break;
      case 'Dashboard':
        nextSearchableComponents = searchableComponent.components;
        break;
      case 'Conditional Block':
        nextSearchableComponents = searchableComponent.components.concat(searchableComponent.otherwise || []);
        break;
      default:
        nextSearchableComponents = [];
        break;
    }

    for (const nextSearchableComponent of nextSearchableComponents) {
      if (recursiveSearch(nextSearchableComponent, currentDepth + 1)) {
        foundComponents.push(searchableComponent);
        return true;
      }
    }

    return false;
  }

  for (const component of survey) {
    if (recursiveSearch(component, 0)) {
      break;
    }
  }

  return foundComponents.reverse();
}

/**
 * Locates the desired leaf node within the Distro Configuration.
 * Starts with the topmost node in the path, and proceeds one level of
 * depth at a time, expanding nodes using the [show] buttons until the leaf is reached.
 * it then scrolls to the leaf!
 */
export async function findAndGoToComponent(tree: string[], survey: Survey) {
  console.log('[scroll-to] expanding survey'); // TODO(Riley): can we just pass in an expanded survey?
  const surveyCopy = expandTemplatesStrict<Survey>(deepCopy(survey));
  console.log('[scroll-to] locating component details', tree);
  const components = getComponentsInPath(surveyCopy, tree);
  console.log('[scroll-to] components found', components);

  const editor = document.getElementsByTagName('distro-editor')[0];
  const root = editor.shadowRoot!;
  const surveyTab = root.querySelector("[data-tab-name=survey]");

  if (surveyTab) surveyTab.dispatchEvent(new Event('click', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));

  const searchComponent = async (depth: number, element: Element | null) => {
    if (depth >= components.length) {
      console.log('scroll-to depth is greater than components length', depth, components.length);
      return;
    }

    // Get object and string properties of the current component.
    const comp = Object.entries(components[depth])
      .filter(([_, v]) => (typeof v === 'object' && !Array.isArray(v) && 'en' in v) || typeof v === 'string');

    // This selector string finds all the parent nodes that
    // contain children which account for every field of the current SearchableComponent
    //
    // Note: There is a nasty bug here where if a component has a @hidden property, it likely won't be found
    // because we remove hidden properties from the object in editor.tsx before rendering, creating a mismatch
    // between the type of a component and its DOM representation.
    // An example of this is a Subsurvey with a redirectToSubsurvey set.
    // This doesn't impact version because it is a number and comp only looks at properties that are strings or objects.
    const excludedHiddenProperties = [
      'activeFilter', // DashboardSection
      'filterPath', // DashboardSection
      'downloadPath', // ApplicantTable / Table
      'enableMeetings', // CommsConfig
      '_id', // RichText / Text
      'id', // Various
      'instructions', // FillableForm
      'macroDefinitions', // ExpandedSurvey
      'numberInQueue', // Queue
      'redirectToSubsurvey', // Subsurvey
      'version', // Various
    ];
    let selector = '[data-tracker-type=object]:has' + comp
      .filter(([k, v]) => !excludedHiddenProperties.includes(k))
      .map(([k, v]) => {
        if (typeof v === 'object' && 'en' in v) {
          k = 'en';
        }
        // Substring length below needs to match ObjectElement substring length in editor.tsx
        // Value is not shared directly to avoid dependencies on editor.tsx.
        return `([${toName(k)}="${CSS.escape((v.en ?? v).substring(0, 100))}"])`
      }).join(':has');

    console.log('scroll-to selector', selector, 'depth', depth);

    // Escaping newlines so the selector string is both valid and still finds the right thing
    const matchedElements: NodeListOf<Element> = (element || root).querySelectorAll(selector);
    if (matchedElements.length === 0) {
      console.log('scroll-to no matched elements');
      return;
    }

    for (let t = 0; t < matchedElements.length; t++) {
      let candidate = matchedElements[t];

      if (depth === components.length - 1) {
        if (matchedElements.length > 1) {
          console.log('scroll-to multiple matched elements at target depth', matchedElements);
          // This is a workaround to avoid an issue where if someone clicks on the same search term multiple times,
          // subsequent clicks find the wrapping element as well as the actual element.
          candidate = matchedElements[matchedElements.length - 1];
        }
        console.log('scroll-to found element at target depth!', candidate, depth);
        candidate.scrollIntoView({ block: 'start', behavior: 'smooth' });
        found = true;
        return;
      }

      // Expand collapsible nodes, excluding 'options[show]' buttons because they do
      // not lead us to further sections of the tree.
      // We use a Timeout to let the browser catch up.
      const showBtn = candidate?.querySelector(
        '#expander:not([data-btn-type=options])'
      );
      if (showBtn?.textContent?.includes('show')) {
        showBtn.dispatchEvent(new Event('click', { bubbles: true }));
      }
      await new Promise(res => setTimeout(res, 100));

      // Recurse to the next depth level
      await searchComponent(depth + 1, candidate);
      if (found) return;
    }
  }

  let found = false;
  try {
    await searchComponent(0, null);
  } catch (e) {
    captureException(new Error('scroll-to failed'), { extra: { cause: e } });
  }
}

export default function ConfigSearch(props: {
  survey: Survey,
  close: () => void
}) {
  const [query, setQuery] = useState('')

  const [open, setOpen] = useState(true)

  const [results, setResults] = useState(null as TreeBlock[] | null);
  const [resultTypes, setResultTypes] = useState<Record<MatchType, TreeBlock[]> | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<Record<MatchType, boolean>>({ "Name/Title/Path": true, "Target Field": true, "Content": true, "Formula": true, "Kind": true, "Comments": true, "Other": true });
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({ matchCase: false, wholeMatchOnly: false, includeDashboards: true, includeComments: false });
  const [searching, setSearching] = useState(false);
  const [showLength, setShowLength] = useState(0);
  const [showThing, setShowThing] = useState({} as Record<string, boolean>); // Used to hide large objects

  async function doSearch() {
    // Using a callback here guarantees that multiple clicks will not cause multiple API calls
    // because React automatically queues the set requests and executes them sequentially.
    setSearching((currentSearching) => {
      if (currentSearching) {
        console.log("Already clicked");
        return currentSearching;
      }
      (async () => {
        if (!query || query.length < 2) {
          setResults(null);
          setResultTypes(null);
          return;
        }

        console.time('findComponent');
        const components = await findComponent(props.survey, query, searchOptions);
        console.timeEnd('findComponent');

        // Group components by type for easy toggling
        const componentTypes = (components || []).reduce<Record<MatchType, TreeBlock[]>>(
          (acc, cur) => {
            for (let matchType of cur.matchTypes) {
              acc[matchType] ||= [];
              acc[matchType].push(cur);
            }
            return acc;
          }, { "Name/Title/Path": [], "Target Field": [], "Content": [], "Formula": [], "Kind": [], "Comments": [], "Other": [] });

        setResults(components || null);
        setShowLength((components?.length || 0) > 20 ? 20 : components?.length || 0);
        setResultTypes(componentTypes);
        setSelectedTypes((Object.keys(componentTypes) as Array<keyof typeof componentTypes>).reduce<Record<MatchType, boolean>>((acc, cur) => {
          acc[cur] = selectedTypes[cur];
          return acc;
        }, { "Name/Title/Path": false, "Target Field": false, "Content": false, "Formula": false, "Kind": false, "Comments": false, "Other": false }));
        setSearching(false);
      })();

      return true;
    });
  }

  useEffect(() => {
    if (!query || query.length < 2) return;

    const timer = setTimeout(() => {
      doSearch();
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [query, searchOptions]);


  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => {
      setQuery('');
      props.close();
    }} appear>
      <Dialog as="div" className="relative mt-20" style={{ zIndex: 100 }} onClose={() => {
        setOpen(false);
        props.close();
      }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mt-20 mx-auto max-w-4xl transform divide-y bg-white divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
              <>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    {/** svg magnifying glass */}
                    <svg className="svg-icon search-icon" aria-labelledby="title desc" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.9 19.7">
                      <title id="title">Search Icon</title>
                      <desc id="desc">A magnifying glass icon.</desc>
                      <g className="search-path" fill="none" stroke="#848F91">
                        <path strokeLinecap="square" d="M18.5 18.3l-5.4-5.4" />
                        <circle cx="8" cy="8" r="7" />
                      </g>
                    </svg>
                  </div>
                  <input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Search..."
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>

                {(query?.length < 2) && (
                  <div className="py-14 px-6 text-center text-sm sm:px-14">
                    <p className="mt-4 font-semibold text-gray-900">Search term must be 2 or more characters</p>
                  </div>
                )}

                {searching ? (
                  <div className="p-6 text-center text-sm sm:px-14">
                    <p className="mt-4 font-semibold text-gray-900">Searching...</p>
                  </div>
                ) : (
                  (query?.length >= 2) && (
                    <div className="px-6 pt-2 text-center text-sm sm:px-14">
                      <h3>Search Options</h3>
                      <div className="mt-4 grid grid-cols-2 gap-y-2 mb-2 justify-items-start items-start">
                        <Switch
                          checked={searchOptions.matchCase}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, matchCase: !searchOptions.matchCase }))}
                          label={'Match Case'} />
                        <Switch
                          checked={searchOptions.includeDashboards}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, includeDashboards: !searchOptions.includeDashboards }))}
                          label={'Include dashboards'} />
                        <Switch
                          checked={searchOptions.wholeMatchOnly}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, wholeMatchOnly: !searchOptions.wholeMatchOnly }))}
                          label={'Whole Match Only'} />
                        <Switch
                          checked={searchOptions.includeComments}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, includeComments: !searchOptions.includeComments }))}
                          label={'Include Comments'} />
                        {resultTypes && (Object.keys(resultTypes) as Array<keyof typeof resultTypes>).map(t => {
                          return <div key={t}>
                            <Switch key={'switch-' + t}
                              checked={selectedTypes[t]}
                              onCheckedChange={() => setSelectedTypes(prev => ({ ...prev, [t]: !prev[t] }))}
                              label={`${t}: ${resultTypes[t].length}`} />
                          </div>
                        })}
                      </div>
                      <h3>Search results</h3>
                      {results?.length ? (
                        <div>
                          <p className="mt-2 text-gray-500">
                            Found {results.length} results for {query}
                          </p>
                          <p className="mt-0 text-gray-400">
                            {/* TODO: we currently show the same result multiple times if it has mutiple result types,
                            for example, if the search term appears in both the targetfield and content for a component
                            this may or may not be desirable, but it can cause this count to say things like: "Showing 12 of 8 results" */}
                            (Showing {(Object.keys(selectedTypes || {}) as Array<keyof typeof resultTypes>).reduce((acc, cur) => {
                              const resultTypeLength = selectedTypes[cur] ? Object.keys(resultTypes?.[cur] || {}).length : 0;
                              return acc + (resultTypeLength < showLength ? resultTypeLength : showLength);
                            }, 0)} of {results.length} results)
                          </p>
                          <div className="mt-6">
                            {resultTypes && (Object.keys(resultTypes) as Array<keyof typeof resultTypes>).map((t, tIdx) => {
                              return <ul className="mt-6" hidden={!selectedTypes[t]} key={t}>
                                {resultTypes[t] && Object.values(resultTypes[t]).slice(0, showLength).map((result, idx) => {
                                  const content = JSON.stringify(result.thing, null, 2);
                                  const uuid = content.replace(/[^a-zA-Z0-9]/g, '') + idx + t + tIdx;

                                  return (
                                    <li key={uuid} className="mt-4 p-4 text-left border-2 border-b-2 border-black">
                                      <span className="inline-flex flex-wrap items-center">{result.tree.map((v, index) => <>
                                        <button
                                          className={classNames(index === (result.tree.length - 1) ? "font-semibold bg-green-300 rounded p-1 hover:animate-pulse" : "font-semibold")}
                                          disabled={index !== (result.tree.length - 1)}
                                          onClick={() => findAndGoToComponent(result.tree, props.survey)}>
                                          {v}
                                        </button>
                                        &nbsp;{index !== (result.tree.length - 1) && <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}&nbsp;
                                      </>)}</span>
                                      {/** This can get really long so if it's really long let's collapse by default */}
                                      {content.length > 500 && <button onClick={() => setShowThing(prev => ({ ...prev, [uuid]: !prev[uuid] }))} className="float-right px-2 py-1 bg-gray-200 hover:bg-gray-300">{showThing[uuid] ? 'Hide' : 'Show'}</button>}
                                      <pre className={classNames("bg-gray-50 my-3 p-2", (content.length > 500 && !showThing[uuid]) ? 'hidden' : '')} style={{ whiteSpace: 'pre-wrap' }}>
                                        {highlightSearchTerm(content, query, searchOptions)}
                                      </pre>
                                      <hr />
                                      {(showLength < Object.values(resultTypes[t]).length) && (idx === showLength - 1) && <button className={BUTTON_CLASS} onClick={() => setShowLength(showLength + 20)}>Show More</button>}
                                    </li>
                                  )
                                })}
                              </ul>
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="py-14 px-6 text-center text-sm sm:px-14">
                          <p className="mt-4 font-semibold text-gray-900">No results found</p>
                          <p className="mt-2 text-gray-500">
                            We couldnt find anything with that term. Please try again.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                )}
              </>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export type FieldMismatchMap = Record<string, { tree: string[], text: Text, fieldsInLangMap: Record<keyof Text, string[]> }>;

/**
 *  Returns a FieldMismatchMap to be used for scrolling to the component
 *  containing a field mismatch via findAndGoToComponent in ConfigSearch.tsx
 */
export function buildFieldMismatchPathMap(
  components: DistroComponent[],
  map: FieldMismatchMap = {}
): FieldMismatchMap {
  const searchForFieldMismatches = (node: any, outerComponent: DistroComponent, tree: string[], map: Record<string, { tree: string[], text: Text, fieldsInLangMap: Record<keyof Text, string[]> }>) => {
    if (typeof node === 'object' && typeof node.en === 'string' && Object.keys(node).length > 1) {
  
      // Create mapping of language code to array of target fields contained in the text for that language
      const fieldsInLangMap: Record<string, string[]> = {};
      Object.entries(node).forEach(([lang, content]) => {
        if (lang === '_id' || lang === '_trackedObjInfo' || typeof content !== 'string') return;
        fieldsInLangMap[lang] = (content.match(FIELD_REF_REGEX) || []);
        if (fieldsInLangMap[lang].length === 0) {
          fieldsInLangMap[lang] = ['[Empty]'];
        }
      });
  
      // For each language that is not English, compare included fields to those in English, 
      let fieldsMatch = true;
      outer: for (const lang of Object.keys(fieldsInLangMap)) {
        if (lang === 'en') continue;
        if (fieldsInLangMap[lang]?.length !== fieldsInLangMap['en']?.length) {
          fieldsMatch = false;
          break;
        }
        for (let i = 0; i < fieldsInLangMap[lang].length; i++) {
          if (!(fieldsInLangMap['en'] || []).includes(fieldsInLangMap[lang][i])) {
            fieldsMatch = false;
            break outer;
          }
        }
      }
  
      if (!fieldsMatch) {
        map[outerComponent.kind + DELIMITER + (('targetField' in outerComponent && outerComponent.targetField) ? outerComponent.targetField : generateUuid(outerComponent))] = { tree, text: node, fieldsInLangMap };
      }
      return;
    }
  
    if (typeof node === 'object') {
      if (Array.isArray(node)) {
        node.flatMap((subNode) => searchForFieldMismatches(subNode, outerComponent, tree, map));
        return;
      }
  
      Object.values(node).flatMap((subNode) => searchForFieldMismatches(subNode, outerComponent, tree, map));
      return;
    }
    return;
  }

  return buildPathMap({
    components,
    map,
    handlePotentialTarget: (component, tree, map) => {
      searchForFieldMismatches(component, component, tree, map);
    },
  });
}

/**
 *  Used to build a tree map function that can be used to find components.
 *  The function recurses through container components like Collections, Sections, Subsurveys, and Conditional Blocks,
 *  and when it finds a non-container component, it calls the handlePotentialTarget function.
 * 
 *  The handlePotentialTarget function should set the map using whatever key makes sense for the need,
 *  with the value including the tree and whatever other data is required from the component.
 */
export function buildPathMap<T>({ components, map, handlePotentialTarget }: {
  components: DistroComponent[],
  map: T,
  handlePotentialTarget: (component: DistroComponent, tree: string[], map: T) => void;
}): T {
  function buildPathInner(components: DistroComponent[], parentTree: string[]) {
    for (const component of components) {
      const componentIdentifier = getComponentIdentifier(component);

      const tree = [...parentTree, component.kind + (componentIdentifier ? DELIMITER + componentIdentifier : '')];

      // Recursively handle children
      if (component.kind === 'Section' || component.kind === 'Collection') {
        buildPathInner(component.components, tree);
      } else if (component.kind === 'Subsurvey') {
        buildPathInner(component.sections, tree);
      } else if (component.kind === 'Conditional Block') {
        buildPathInner(component.components, tree);
        if (component.otherwise) {
          buildPathInner(component.otherwise, tree);
        }
      } else {
        handlePotentialTarget(component, tree, map);
      }
    }
  }
  buildPathInner(components, []);
  return map;
}
